import React, { useRef, useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
	Chart as ChartJS,
	ArcElement,
	Tooltip,
	Legend,
} from "chart.js";
import { api } from '../../api/apiService';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const DashboardOverview = () => {
	const chartRef = useRef(null);
	const [chartData, setChartData] = useState({ labels: [], datasets: [] });
	const [summary, setSummary] = useState(null);
	const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);

	const totalEmployees = summary?.total_employees ?? 0;
	const presentEmployees = summary?.present_employees ?? 0;
	const lateEmployees = summary?.late_employees ?? 0;
	const absentEmployees = summary?.absent_employees ?? 0;

	useEffect(() => {
		const fetchSummary = async () => {
			const url = selectedDate ? `/reports/charts/summary?date=${selectedDate}` : '/reports/charts/summary';
			const response = await api.get(url);
			const payload = response.data?.data ?? response.data;
			const summaryPayload = response.data?.summary ?? null;
			setSummary(summaryPayload);
			const labels = payload?.labels || [];
			const ds = payload?.datasets?.[0] || { data: [], backgroundColor: [], borderWidth: 0 };
			const filtered = labels
				.map((label, idx) => ({ label, value: ds.data?.[idx], color: ds.backgroundColor?.[idx] }))
				.filter(item => item.label !== 'Total');
			setChartData({
				labels: filtered.map(i => i.label),
				datasets: [
					{
						data: filtered.map(i => i.value),
						backgroundColor: filtered.map(i => i.color),
						borderWidth: 0
					}
				]
			});
		};
		fetchSummary();
	}, [selectedDate]);

	const data = chartData;

	const options = {
		cutout: "75%",
		plugins: {
			legend: {
				display: true,
			},
			datalabels: {
				display: false,
			},
		},
	};

	const roundedPlugin = {
		id: "roundedCorners",
		afterUpdate(chart) {
			const arcs = chart.getDatasetMeta(0).data;
			arcs.forEach((arc) => {
				arc.round = {
					x: (chart.chartArea.left + chart.chartArea.right) / 2,
					y: (chart.chartArea.top + chart.chartArea.bottom) / 2,
					radius: (arc.outerRadius + arc.innerRadius) / 2,
					thickness: (arc.outerRadius - arc.innerRadius) / 2,
					backgroundColor: arc.options.backgroundColor,
				};
			});
		},
		afterDraw(chart) {
			const { ctx } = chart;
			chart.getDatasetMeta(0).data.forEach((arc) => {
				const endAngle = Math.PI / 2 - arc.endAngle;
				ctx.save();
				ctx.translate(arc.round.x, arc.round.y);
				ctx.fillStyle = arc.round.backgroundColor;
				ctx.beginPath();
				ctx.arc(
					arc.round.radius * Math.sin(endAngle),
					arc.round.radius * Math.cos(endAngle),
					arc.round.thickness,
					0,
					2 * Math.PI
				);
				ctx.closePath();
				ctx.fill();
				ctx.restore();
			});
		},
	};

	return (
		<div>
			<div className="mt-4 d-flex justify-content-between align-items-center mb-2">
				<h4 className="text-muted text-sm font-semibold">Overview</h4>
				<input
					type="date"
					className="chart-dropdown"
					value={selectedDate}
					onChange={(e) => setSelectedDate(e.target.value)}
				/>
			</div>

			<div className="d-flex justify-content-center mb-4">
				<div className="position-relative" style={{ width: '250px', height: '250px' }}>
					<Doughnut
						ref={chartRef}
						data={data}
						options={options}
						plugins={[roundedPlugin]}
					/>
					<div className="position-absolute start-50 translate-middle text-center" style={{ top: '60%' }}>
						<div className="fw-bold fs-4 text-dark mb-1">
							{summary?.absent_percentage ?? 0}%
						</div>
						<div className="text-muted fs-6 fw-semibold">
							Absent
						</div>
					</div>
				</div>
			</div>
			
			<div className="row mt-4">
				<div className="col-6 d-flex align-items-center mb-3">
					<i className="bi bi-circle-fill text-light me-2"></i>
					<div className="d-flex  align-items-start">
						<span className="fw-bold text-black">{totalEmployees} </span>
						 <span className="text-muted">Total</span>
					</div>
				</div>

				<div className="col-6 d-flex align-items-center mb-3">
					<i className="bi bi-circle-fill text-success me-2"></i>
					<div className="d-flex  align-items-start">
						<span className="fw-bold text-black">{presentEmployees} </span>
						<span className="text-muted">Present</span>
					</div>
				</div>

				<div className="col-6 d-flex align-items-center mb-3">
					<i className="bi bi-circle-fill text-warning me-2"></i>
					<div className="d-flex  align-items-start">
						<span className="fw-bold text-black">{lateEmployees}</span>
						<span className="text-muted">Late</span>
					</div>
				</div>

				<div className="col-6 d-flex align_items-center mb-3">
					<i className="bi bi-circle-fill text-danger me-2"></i>
					<div className="d-flex  align-items-start">
						<span className="fw-bold text-black">{absentEmployees}</span>
						<span className="text-muted">Absent</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DashboardOverview;

